import { useCallback, useMemo } from "react";
import {
  AlertTriangle,
  Building2,
  ClipboardCheck,
  Droplets,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import HeroStatusChip from "@/components/HeroStatusChip";
import MistralGuidanceChip from "@/components/MistralGuidanceChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { buildClassroomsToiletsRiskMarkdown } from "@/utils/buildClassroomsToiletsLocalGuidance";
import { toTitleCase } from "../utils/toTitleCase";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const OWNERS = ["BEO", "Engineer", "Cluster"];

function normalizeSchoolTypeCode(code) {
  const n = Number(code);
  if (n === 1 || n === 2 || n === 3) return n;
  return 3;
}

function inferSchoolTypeFromName(name) {
  if (name == null || String(name).trim() === "") return null;
  const s = String(name).toLowerCase();
  const boysLike = /\b(boys?|balak)\b/i.test(s);
  const girlsLike = /\b(girls?|kanya|balika|kanyashala)\b/i.test(s);
  if (boysLike && !girlsLike) return 1;
  if (girlsLike && !boysLike) return 2;
  return null;
}

function normalizeEffectiveSchoolType(school) {
  const eff = school?.effective_school_type;
  if (eff !== undefined && eff !== null && eff !== "") {
    const n = Number(eff);
    if (n === 1 || n === 2 || n === 3) return n;
  }
  const fromCode = normalizeSchoolTypeCode(school?.school_type_code);
  if (fromCode !== 3) return fromCode;
  const inferred = inferSchoolTypeFromName(school?.school_name);
  if (inferred !== null) return inferred;
  return 3;
}

const getRiskReason = (school) => {
  const st = normalizeEffectiveSchoolType(school);
  const boysRequired = st !== 2;
  const girlsRequired = st !== 1;
  const boysGap = boysRequired && Number(school.boys_toilets_total) === 0;
  const girlsGap = girlsRequired && Number(school.girls_toilets_total) === 0;
  if (boysGap && girlsGap) return "Boys + Girls toilet missing";
  if (boysGap) return "Boys toilet missing";
  if (girlsGap) return "Girls toilet missing";
  return "Toilet gap";
};

const getOwner = (udise, index) => OWNERS[(Number(String(udise).slice(-2)) + index) % OWNERS.length];

const getDueDays = (reason) => (reason.includes("Boys + Girls") ? 7 : 10);

const buildRiskGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ct-danger) 0 ${Math.min(value, 100)}%, rgba(239,68,68,.14) ${Math.min(value, 100)}% 100%)`,
});

const computeRiskLoad = (summary) => {
  if (!summary) return 0;
  const total =
    (summary.zero_toilet_pct ?? 0) +
    (summary.no_water_pct ?? 0) +
    (summary.no_handwash_pct ?? 0) +
    (summary.major_repair_pct ?? 0);
  return Math.min(100, Math.round(total * 5));
};

const bucketProgress = (count, totalSchools) =>
  Math.min(100, Math.round((count / Math.max(totalSchools, 1)) * 900 + 35));

const getMatrixTone = (gapScore, repairBurden) => {
  if (gapScore >= 12 && repairBurden >= 12) return "red";
  if (gapScore >= 8 || repairBurden >= 10) return "orange";
  return "";
};

const BucketCard = ({ icon: Icon, iconTone, dotTone, title, count, label, progress, progressColor, metaLeft, metaRight }) => (
  <article className="ct-bucket-card">
    <div className="ct-bucket-head">
      <div className={`ct-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`ct-priority-dot ${dotTone}`} />
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="ct-bucket-value">
      <strong>{fmt(count)}</strong>
      <span>{label}</span>
    </div>
    <div className="ct-progress-line">
      <i style={{ width: progress, background: progressColor }} />
    </div>
    <div className="ct-bucket-meta">
      <span>{metaLeft}</span>
      <span>{metaRight}</span>
    </div>
  </article>
);

const ClassroomsToiletsRiskActionTab = ({ riskSchools, topBottomBlocks, blockData }) => {
  const hasData = Boolean(riskSchools?.risk_summary);

  const buildLocalMarkdown = useCallback(
    () => buildClassroomsToiletsRiskMarkdown({ riskSchools, topBottomBlocks, blockData }),
    [riskSchools, topBottomBlocks, blockData]
  );

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        z: riskSchools?.risk_summary?.zero_toilet_schools,
        w: riskSchools?.risk_summary?.no_water_schools,
      }),
    [riskSchools?.risk_summary?.zero_toilet_schools, riskSchools?.risk_summary?.no_water_schools]
  );

  const {
    loading: aiLoading,
    pickFromBucket,
    pickPrimaryAction,
    provider,
  } = useMistralNarrativeOverlay({
    kind: "classrooms-toilets-risk",
    title: "Classrooms & Toilets Risk Command Centre",
    buildLocalMarkdown,
    context: { riskSchools, topBottomBlocks, blockData },
    enabled: hasData,
    scopeKey,
  });

  if (!hasData) {
    return (
      <div className="ct-empty-card">
        <AlertTriangle className="mx-auto mb-3 opacity-40" size={40} />
        <p>No risk and action data available for the selected scope.</p>
      </div>
    );
  }

  const summary = riskSchools.risk_summary;
  const totalSchools = summary.total_schools ?? 0;
  const riskLoad = computeRiskLoad(summary);

  const zeroToilet = summary.zero_toilet_schools ?? 0;
  const noWater = summary.no_water_schools ?? 0;
  const noHandwash = summary.no_handwash_schools ?? 0;
  const majorRepair = summary.major_repair_schools ?? 0;

  const topBlocks = topBottomBlocks?.top_blocks?.slice(0, 3) ?? [];
  const bottomBlocks = topBottomBlocks?.bottom_blocks?.slice(0, 2) ?? [];

  const matrixBlocks = [...(blockData ?? [])]
    .map((block) => {
      const toiletGap = 100 - (block.functional_pct ?? 0);
      const waterGap = 100 - (block.water_pct ?? 0);
      const gapScore = (toiletGap + waterGap) / 2;
      const repairBurden =
        typeof block.repair_backlog_pct === "number"
          ? block.repair_backlog_pct
          : typeof block.classroom_health_index === "number"
            ? Math.max(0, 100 - block.classroom_health_index)
            : 0;
      const tone = getMatrixTone(gapScore, repairBurden);
      return {
        name: block.block_name,
        gapScore,
        repairBurden,
        tone,
        left: Math.min(82, Math.max(18, 18 + gapScore * 0.65)),
        top: Math.min(78, Math.max(22, 78 - repairBurden * 0.55)),
      };
    })
    .sort((a, b) => b.gapScore + b.repairBurden - (a.gapScore + a.repairBurden))
    .slice(0, 6);

  const localHeroNarrative =
    "Critical exceptions are now grouped by urgency, block concentration and closure workflow so officers can move from risk detection to action without reading long raw tables first.";
  const heroNarrative = aiLoading
    ? localHeroNarrative
    : pickFromBucket(localHeroNarrative, "insights", 0) || localHeroNarrative;

  const localPrimaryAction =
    "Prioritize schools with both boys and girls toilet gaps first, then no-water and no-handwash schools for WASH compliance closure.";
  const primaryAction = aiLoading
    ? localPrimaryAction
    : pickPrimaryAction(localPrimaryAction) || localPrimaryAction;

  const actionQueue = (riskSchools.zero_toilet_schools ?? []).slice(0, 20).map((school, index) => {
    const reason = getRiskReason(school);
    return {
      ...school,
      risk_reason: reason,
      owner: getOwner(school.udise_code, index),
      due_days: getDueDays(reason),
    };
  });

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleExport = () => {
    const rows = [
      ["UDISE", "School Name", "Block", "Risk Reason", "Owner", "Due Days", "Status"],
      ...actionQueue.map((s) => [
        s.udise_code,
        s.school_name,
        s.block_name,
        s.risk_reason,
        s.owner,
        `${s.due_days} days`,
        "Critical",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "critical-school-action-queue.csv";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Exception list exported");
  };

  return (
    <div className="ct-risk-tab" data-testid="tab-risk-content" id="ct-risk-overview">
      <section className="ct-hero-card ct-hero-card-risk">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-risk-main">
              <div
                className="ct-risk-gauge"
                style={buildRiskGaugeStyle(riskLoad)}
                aria-label={`Risk load ${pct(riskLoad, 0)}`}
              >
                <div>
                  <strong>{pct(riskLoad, 0)}</strong>
                  <span>Risk Load</span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>Risk & Action Command Centre</h2>
                <p>{heroNarrative}</p>
                <div className="ct-status-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                  <MistralGuidanceChip provider={provider} loading={aiLoading} />
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="red"
                    onClick={() => scrollTo("ct-risk-diagnostics")}
                    ariaLabel={`${zeroToilet} toilet-gap schools — view risk diagnostics`}
                  >
                    {fmt(zeroToilet)} toilet-gap schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={() => scrollTo("ct-risk-diagnostics")}
                    ariaLabel={`${noWater} no-water schools — view risk diagnostics`}
                  >
                    {fmt(noWater)} no-water schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="blue"
                    onClick={() => scrollTo("ct-risk-action-queue")}
                    ariaLabel="High-risk action queue — view recommended actions"
                  >
                    High-risk action queue ready
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary officer action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="ct-metric-grid ct-metric-grid-2" aria-label="Risk KPIs">
            <article className="ct-metric-card ct-metric-card-risk">
              <small>Schools with toilet gap</small>
              <strong>{fmt(zeroToilet)}</strong>
              <span className="ct-metric-sub">{pct(summary.zero_toilet_pct ?? 0, 2)} of schools need WC closure</span>
            </article>
            <article className="ct-metric-card ct-metric-card-risk orange">
              <small>No water schools</small>
              <strong>{fmt(noWater)}</strong>
              <span className="ct-metric-sub">{pct(summary.no_water_pct ?? 0, 2)} of schools</span>
            </article>
            <article className="ct-metric-card ct-metric-card-risk orange">
              <small>No handwash</small>
              <strong>{fmt(noHandwash)}</strong>
              <span className="ct-metric-sub">{pct(summary.no_handwash_pct ?? 0, 2)} of total schools</span>
            </article>
            <article className="ct-metric-card ct-metric-card-risk purple">
              <small>Major repair needed</small>
              <strong>{fmt(majorRepair)}</strong>
              <span className="ct-metric-sub">{pct(summary.major_repair_pct ?? 0, 2)} of schools</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Priority Risk Buckets</h2>
          <p>
            Risk is separated into actionable buckets instead of flat KPI cards, making it easier to assign
            owners, deadlines and closure evidence.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-risk-action-queue")}>
          Open action queue →
        </button>
      </div>

      <section className="ct-risk-bucket-grid">
        <BucketCard
          icon={Building2}
          iconTone="red"
          dotTone=""
          title="Sanitation gap closure"
          count={zeroToilet}
          label="schools"
          progress={`${bucketProgress(zeroToilet, totalSchools)}%`}
          progressColor="var(--ct-danger)"
          metaLeft="Priority: Critical"
          metaRight="7-day triage"
        />
        <BucketCard
          icon={Droplets}
          iconTone="orange"
          dotTone="amber"
          title="Water availability gap"
          count={noWater}
          label="schools"
          progress={`${bucketProgress(noWater, totalSchools)}%`}
          progressColor="var(--ct-warning)"
          metaLeft="Priority: High"
          metaRight="15-day closure"
        />
        <BucketCard
          icon={Wrench}
          iconTone="purple"
          dotTone="amber"
          title="Major repair backlog"
          count={majorRepair}
          label="schools"
          progress={`${bucketProgress(majorRepair, totalSchools)}%`}
          progressColor="var(--ct-purple)"
          metaLeft="Priority: High"
          metaRight="Budget tagging"
        />
        <BucketCard
          icon={ClipboardCheck}
          iconTone="green"
          dotTone="green"
          title="Evidence verification"
          count={0}
          label="pending uploads"
          progress="94%"
          progressColor="var(--ct-success)"
          metaLeft="Priority: Monitor"
          metaRight="Photo + geo-tag"
        />
      </section>

      <div className="ct-section-title" id="ct-risk-diagnostics">
        <div>
          <h2>Block Risk Diagnostics</h2>
          <p>
            Top and bottom performers are presented together with a risk matrix to show where action teams should
            focus first.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-risk-action-queue")}>
          View critical schools ↓
        </button>
      </div>

      <section className="ct-analytics-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Top vs Bottom Block Performance</h3>
              <p>Best blocks are used as benchmarks; bottom blocks are converted into intervention clusters.</p>
            </div>
            <span className="ct-pill">Score</span>
          </div>
          <div className="ct-ranking-list">
            {topBlocks.map((block, index) => (
              <div className="ct-rank-row" key={block.block_name}>
                <span className="ct-rank-badge">{index + 1}</span>
                <div className="ct-rank-copy">
                  <b>{block.block_name}</b>
                  <span>
                    Health {pct(block.classroom_health)} • Toilet {pct(block.toilet_functional_pct)}
                  </span>
                </div>
                <span className="ct-score-chip">{block.composite_score?.toFixed(1)}</span>
              </div>
            ))}
            {bottomBlocks.map((block) => (
              <div className="ct-rank-row warn" key={`bottom-${block.block_name}`}>
                <span className="ct-rank-badge">!</span>
                <div className="ct-rank-copy">
                  <b>{block.block_name}</b>
                  <span>
                    Health {pct(block.classroom_health)} • Toilet {pct(block.toilet_functional_pct)}
                  </span>
                </div>
                <span className="ct-score-chip">{block.composite_score?.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Risk Concentration Matrix</h3>
              <p>Blocks closer to the top-right require immediate intervention and field verification.</p>
            </div>
            <span className="ct-pill ct-pill-red">Criticality</span>
          </div>
          <div className="ct-matrix" aria-label="Risk concentration matrix">
            <div className="ct-matrix-legend">
              <span>
                <i className="ct-dot red" /> Critical
              </span>
              <span>
                <i className="ct-dot orange" /> High
              </span>
              <span>
                <i className="ct-dot blue" /> Monitor
              </span>
            </div>
            <div className="ct-matrix-grid" />
            <span className="ct-axis y">Repair burden</span>
            <span className="ct-axis x">Toilet + water gap</span>
            {matrixBlocks.map((block) => (
              <div key={block.name}>
                <div
                  className={`ct-risk-point ${block.tone}`}
                  style={{ left: `${block.left}%`, top: `${block.top}%` }}
                  title={`${block.name}: gap ${block.gapScore.toFixed(1)}, repair ${block.repairBurden.toFixed(1)}`}
                />
                <span className="ct-risk-label" style={{ left: `${block.left}%`, top: `${block.top}%` }}>
                  {block.name}
                </span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>High-Risk Schools and Action Ownership</h2>
          <p>
            The long exception table is redesigned as a closure queue with owner, due date, risk reason and action
            button.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={handleExport}>
          Export exception list
        </button>
      </div>

      <section className="ct-two-column" id="ct-risk-action-queue">
        <article className="ct-panel ct-table-card">
          <div className="ct-table-head">
            <div>
              <h3>Critical School Action Queue</h3>
              <p>Schools with missing boys/girls toilet facilities or immediate WASH risks.</p>
            </div>
            <span className="ct-pill ct-pill-red">Critical</span>
          </div>
          <div className="ct-table-wrap">
            <table className="ct-infra-table" aria-label="Critical school action queue">
              <thead>
                <tr>
                  <th>UDISE</th>
                  <th>School Name</th>
                  <th>Block</th>
                  <th>Risk Reason</th>
                  <th>Owner</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {actionQueue.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", color: "var(--ct-muted)", fontWeight: 600 }}>
                      No critical schools in the selected scope.
                    </td>
                  </tr>
                ) : (
                  actionQueue.map((school) => (
                    <tr key={school.udise_code}>
                      <td>{school.udise_code}</td>
                      <td>{school.school_name}</td>
                      <td>{school.block_name}</td>
                      <td>{school.risk_reason}</td>
                      <td>
                        <span className="ct-owner-tag">{school.owner}</span>
                      </td>
                      <td className="ct-due">{school.due_days} days</td>
                      <td>
                        <span className="ct-risk-tag">Critical</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ct-action-link"
                          onClick={() => toast.info(`Assigned ${school.school_name} to ${school.owner}`)}
                        >
                          Assign
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>30-Day Closure Plan</h3>
              <p>Recommended officer workflow for risk resolution.</p>
            </div>
            <span className="ct-pill">Action</span>
          </div>
          <div className="ct-action-card">
            <div className="ct-risk-action-item">
              <div className="ct-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <h4>Day 0–7: Critical toilet gap closure</h4>
                <p>
                  Validate schools with zero boys or girls toilets, assign block owner and capture geo-tagged
                  evidence.
                </p>
              </div>
            </div>
            <div className="ct-risk-action-item">
              <div className="ct-icon-box orange">
                <Droplets size={22} />
              </div>
              <div>
                <h4>Day 8–15: Water and handwash remediation</h4>
                <p>Bundle water availability and handwash-point fixes under one WASH service ticket.</p>
              </div>
            </div>
            <div className="ct-risk-action-item">
              <div className="ct-icon-box purple">
                <Wrench size={22} />
              </div>
              <div>
                <h4>Day 16–30: Repair and budget tracking</h4>
                <p>Move major repairs into estimate, approval, work order and closure certificate stages.</p>
              </div>
            </div>
            <div className="ct-action-footer ct-action-footer-risk">
              <b>Review cadence</b>
              <span>
                Use weekly district review to track closed schools, pending evidence, escalated budget items and
                unresolved critical exceptions.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default ClassroomsToiletsRiskActionTab;
